import { DateTime } from 'luxon';

import type {
  ArrivalsDeparturesView,
  DashboardArrivalsDeparturesPayload,
  DashboardOccupancyRowPayload,
  DashboardRatingsPayload,
  DashboardReportPayload,
  DashboardSentimentPayload,
  DashboardView,
  OccupancyPointView,
  OccupancyView,
  RatingBucketView,
  RatingsView,
  RatingValue,
  SentimentView,
} from '../interfaces/dashboard';

/** The only rating values the product supports. */
const RATING_VALUES: RatingValue[] = [1, 2, 3, 4, 5];

/**
 * Coerce an API numeric that may arrive as a JSON number or as a string
 * ("155", "0.00"). Returns `fallback` for null/undefined/empty/NaN/Infinity, so
 * a NaN can never reach a chart axis — Recharts renders `path d="NaN"` for one,
 * which produces a silently invisible chart.
 *
 * Deliberately not `lodash.toNumber`, which returns NaN for these inputs.
 */
export const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (trimmed === '') {
      return fallback;
    }

    const parsed = Number(trimmed);

    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
};

/** '—' for an unknown value, so a missing denominator never renders as '0.0%'. */
export const formatPercent = (value: number | null, digits = 1): string =>
  value === null ? '—' : `${value.toFixed(digits)}%`;

export const formatCount = (value: number): string => value.toLocaleString();

/**
 * Resolve the calendar month of an occupancy row. The API sends a numeric
 * `month` today; the `period` string ('August, 2026') is the fallback in case a
 * grouping other than monthly is ever returned.
 */
const resolveMonth = (row: DashboardOccupancyRowPayload): number | undefined => {
  const explicit = toNumber(row.month, 0);

  if (explicit >= 1 && explicit <= 12) {
    return explicit;
  }

  const parsed = DateTime.fromFormat(row.period, 'LLLL, yyyy');

  return parsed.isValid ? parsed.month : undefined;
};

interface OccupancyBucket {
  period: string;
  year: number;
  month?: number;
  nightsAvailable: number;
  nightsOccupied: number;
}

/**
 * Normalize the occupancy slice.
 *
 * Rows are grouped by `period` and the nights are *summed*: the response can
 * carry one row per property, and the API's own `occupancyRate` is discarded
 * because averaging per-property rates is wrong when properties have different
 * room counts. The rate is recomputed from the summed nights instead.
 *
 * The API returns rows newest-first; the chart needs them chronological.
 */
export const toOccupancyView = (
  rows: DashboardOccupancyRowPayload[] | undefined,
  now: DateTime = DateTime.now(),
): OccupancyView => {
  const buckets = new Map<string, OccupancyBucket>();

  for (const row of rows ?? []) {
    const existing = buckets.get(row.period);

    if (existing) {
      existing.nightsAvailable += toNumber(row.nightsAvailable);
      existing.nightsOccupied += toNumber(row.totalNightsOccupied);
      continue;
    }

    buckets.set(row.period, {
      period: row.period,
      year: toNumber(row.year, now.year),
      month: resolveMonth(row),
      nightsAvailable: toNumber(row.nightsAvailable),
      nightsOccupied: toNumber(row.totalNightsOccupied),
    });
  }

  const currentSortKey = now.year * 100 + now.month;

  const points: OccupancyPointView[] = Array.from(buckets.values())
    .map((bucket) => {
      const sortKey = bucket.year * 100 + (bucket.month ?? 0);
      const label = bucket.month
        ? DateTime.fromObject({ year: bucket.year, month: bucket.month }).toFormat('LLL')
        : bucket.period;

      return {
        period: bucket.period,
        label,
        sortKey,
        nightsAvailable: bucket.nightsAvailable,
        nightsOccupied: bucket.nightsOccupied,
        occupancyRate:
          bucket.nightsAvailable > 0
            ? (bucket.nightsOccupied / bucket.nightsAvailable) * 100
            : 0,
        isFuture: sortKey > currentSortKey,
        isCurrent: sortKey === currentSortKey,
      };
    })
    .sort((a, b) => a.sortKey - b.sortKey || a.period.localeCompare(b.period));

  const elapsed = points.filter((point) => !point.isFuture);
  const elapsedAvailable = elapsed.reduce((sum, point) => sum + point.nightsAvailable, 0);
  const elapsedOccupied = elapsed.reduce((sum, point) => sum + point.nightsOccupied, 0);

  return {
    year:
      points.length > 0 ? Math.floor(points[points.length - 1].sortKey / 100) : now.year,
    points,
    yearToDateRate: elapsedAvailable > 0 ? (elapsedOccupied / elapsedAvailable) * 100 : 0,
    hasData: points.some((point) => point.nightsAvailable > 0),
  };
};

/**
 * Normalize the ratings slice. The API omits rating values that nobody gave, so
 * the spread is backfilled to all of 1..5 — otherwise the distribution chart
 * silently drops rows and reads as if those values don't exist.
 */
export const toRatingsView = (
  payload: DashboardRatingsPayload | undefined,
): RatingsView => {
  const counts = new Map<number, number>();

  for (const item of payload?.ratingSpread ?? []) {
    const value = Math.round(toNumber(item.value));

    if (value >= 1 && value <= 5) {
      counts.set(value, (counts.get(value) ?? 0) + toNumber(item.count));
    }
  }

  const spreadTotal = Array.from(counts.values()).reduce((sum, count) => sum + count, 0);

  const spread: RatingBucketView[] = RATING_VALUES.map((value) => {
    const count = counts.get(value) ?? 0;

    return {
      value,
      label: value === 1 ? '1 star' : `${value} stars`,
      count,
      // Off the spread's own total, so the bars stay internally consistent even
      // if `ratedCount` disagrees. `ratedCount` drives only the rated meter.
      percent: spreadTotal > 0 ? (count / spreadTotal) * 100 : 0,
    };
  });

  const processedCount = toNumber(payload?.processedCount);
  const ratedCount = toNumber(payload?.ratedCount);

  return {
    averageRating: toNumber(payload?.averageRating),
    processedCount,
    ratedCount,
    notRatedCount: Math.max(processedCount - ratedCount, 0),
    percentRated: toNumber(payload?.percentRated),
    percentNotRated: toNumber(payload?.percentNotRated),
    spread,
    hasRatings: ratedCount > 0 || spreadTotal > 0,
  };
};

export const toArrivalsView = (
  payload: DashboardArrivalsDeparturesPayload | undefined,
): ArrivalsDeparturesView => {
  const totalRooms = toNumber(payload?.totalRooms);

  return {
    date: payload?.date ?? '',
    arrivals: toNumber(payload?.arrivals),
    departures: toNumber(payload?.departures),
    inHouse: toNumber(payload?.inHouse),
    totalRooms,
    // A property with no rooms has no meaningful allocation — '—', never 0%.
    allocationPercent: totalRooms > 0 ? toNumber(payload?.allocationPercent) : null,
  };
};

export const toSentimentView = (
  payload: DashboardSentimentPayload | null | undefined,
): SentimentView | null => {
  if (!payload) {
    return null;
  }

  return {
    score: toNumber(payload.sentimentScore),
    label: payload.sentimentLabel,
    summary: payload.summary,
    sampleSize: toNumber(payload.sampleSize),
    lastProcessedTimestamp: payload.lastProcessedTimestamp,
    periodStart: payload.periodStart,
  };
};

export const toDashboardView = (
  payload: DashboardReportPayload | undefined,
): DashboardView => ({
  ratings: toRatingsView(payload?.ratings),
  occupancy: toOccupancyView(payload?.occupancy),
  arrivals: toArrivalsView(payload?.arrivalsDepartures),
  sentiment: toSentimentView(payload?.sentiment),
});
